import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlayCircle, Pause, StopCircle, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeployTab = () => {
  const [deployments, setDeployments] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [selectedBroker, setSelectedBroker] = useState('');

  useEffect(() => {
    fetchDeployments();
    fetchStrategies();
    fetchBrokers();
  }, []);

  const fetchDeployments = async () => {
    try {
      const response = await axios.get(`${API}/deploy`, { withCredentials: true });
      setDeployments(response.data);
    } catch (error) {
      console.error('Failed to fetch deployments:', error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const response = await axios.get(`${API}/strategies`, { withCredentials: true });
      setStrategies(response.data);
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    }
  };

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${API}/brokers`, { withCredentials: true });
      setBrokers(response.data);
    } catch (error) {
      console.error('Failed to fetch brokers:', error);
    }
  };

  const deployStrategy = async () => {
    if (!selectedStrategy || !selectedBroker) {
      toast.error('Please select strategy and broker');
      return;
    }

    try {
      await axios.post(`${API}/deploy`, {
        strategy_id: selectedStrategy,
        broker_id: selectedBroker
      }, { withCredentials: true });

      toast.success('Strategy deployed successfully!');
      setShowDialog(false);
      setSelectedStrategy('');
      setSelectedBroker('');
      fetchDeployments();
    } catch (error) {
      toast.error('Failed to deploy strategy');
    }
  };

  const pauseDeployment = async (deployId) => {
    try {
      await axios.post(`${API}/deploy/${deployId}/pause`, {}, { withCredentials: true });
      toast.success('Strategy paused successfully');
      
      // Update status immediately
      setDeployments(prevDeployments => 
        prevDeployments.map(d => 
          d.id === deployId ? { ...d, status: 'paused' } : d
        )
      );
      
      setTimeout(fetchDeployments, 500);
    } catch (error) {
      console.error('Pause deployment error:', error);
      toast.error('Failed to pause deployment');
    }
  };

  const resumeDeployment = async (deployId) => {
    try {
      await axios.post(`${API}/deploy/${deployId}/resume`, {}, { withCredentials: true });
      toast.success('Strategy resumed successfully');
      
      // Update status immediately
      setDeployments(prevDeployments => 
        prevDeployments.map(d => 
          d.id === deployId ? { ...d, status: 'active' } : d
        )
      );
      
      setTimeout(fetchDeployments, 500);
    } catch (error) {
      console.error('Resume deployment error:', error);
      toast.error('Failed to resume deployment');
    }
  };

  const stopDeployment = async (deployId) => {
    const deployment = deployments.find(d => d.id === deployId);
    const strategy = strategies.find(s => s.id === deployment?.strategy_id);
    
    if (!window.confirm(`Are you sure you want to stop "${strategy?.name}"? This will close all positions and stop the strategy.`)) return;
    
    try {
      await axios.post(`${API}/deploy/${deployId}/stop`, {}, { withCredentials: true });
      toast.success('Strategy stopped and positions closed');
      
      // Remove from list immediately
      setDeployments(prevDeployments => prevDeployments.filter(d => d.id !== deployId));
      
      setTimeout(fetchDeployments, 500);
    } catch (error) {
      console.error('Stop deployment error:', error);
      toast.error('Failed to stop deployment');
    }
  };

  return (
    <Card className="p-8 bg-white/80 backdrop-blur-sm border-gray-200" data-testid="deploy-tab-content">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Deploy & Monitor
        </h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="deploy-new-btn">
              <Plus className="w-4 h-4 mr-2" />
              Deploy Strategy
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deploy Strategy</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select Strategy</label>
                <Select value={selectedStrategy} onValueChange={setSelectedStrategy}>
                  <SelectTrigger data-testid="strategy-select">
                    <SelectValue placeholder="Choose strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    {strategies.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Select Broker</label>
                <Select value={selectedBroker} onValueChange={setSelectedBroker}>
                  <SelectTrigger data-testid="deploy-broker-select">
                    <SelectValue placeholder="Choose broker" />
                  </SelectTrigger>
                  <SelectContent>
                    {brokers.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.broker_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={deployStrategy} className="w-full" data-testid="confirm-deploy-btn">
                Deploy
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {deployments.length === 0 ? (
        <div className="text-center py-12">
          <PlayCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No active deployments</p>
          <p className="text-sm text-gray-400">Deploy a strategy from Library or My Strategies to start trading</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {deployments.map((deployment) => {
            const strategy = strategies.find(s => s.id === deployment.strategy_id);
            const broker = brokers.find(b => b.id === deployment.broker_id);
            return (
              <div key={deployment.id} className="p-6 bg-gradient-to-br from-white to-slate-50 rounded-xl border border-gray-200 hover:border-indigo-200 transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{strategy?.name || 'Unknown Strategy'}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        deployment.status === 'active' ? 'bg-green-100 text-green-700' :
                        deployment.status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {deployment.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {strategy?.description?.substring(0, 100)}...
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>🏦 {broker?.broker_name || 'Unknown Broker'}</span>
                      <span>📅 {new Date(deployment.deployed_at).toLocaleDateString()}</span>
                      <span>⏰ {new Date(deployment.deployed_at).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>

                {/* Strategy Stats */}
                <div className="grid grid-cols-4 gap-3 mb-4 p-3 bg-slate-100 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Capital</p>
                    <p className="text-sm font-semibold text-gray-900">₹{(strategy?.advanced_settings?.capital_required || 50000).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Check Interval</p>
                    <p className="text-sm font-semibold text-gray-900">{strategy?.advanced_settings?.check_conditions_every || '1 min'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Position Size</p>
                    <p className="text-sm font-semibold text-gray-900">{strategy?.strategy_set?.entry_positions?.[0]?.qty || 0} lots</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Instrument</p>
                    <p className="text-sm font-semibold text-gray-900">{strategy?.strategy_set?.entry_positions?.[0]?.underlying || 'N/A'}</p>
                  </div>
                </div>

                {/* Control Buttons */}
                <div className="flex gap-2">
                  {deployment.status === 'active' && (
                    <Button
                      onClick={() => pauseDeployment(deployment.id)}
                      variant="outline"
                      size="sm"
                      data-testid={`pause-btn-${deployment.id}`}
                    >
                      <Pause className="w-4 h-4 mr-2" />
                      Pause
                    </Button>
                  )}
                  {deployment.status === 'paused' && (
                    <Button
                      onClick={() => resumeDeployment(deployment.id)}
                      className="bg-green-600 hover:bg-green-700"
                      size="sm"
                      data-testid={`resume-btn-${deployment.id}`}
                    >
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Resume
                    </Button>
                  )}
                  <Button
                    onClick={() => stopDeployment(deployment.id)}
                    variant="destructive"
                    size="sm"
                    data-testid={`stop-btn-${deployment.id}`}
                  >
                    <StopCircle className="w-4 h-4 mr-2" />
                    Stop
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default DeployTab;
